/**
 * Sidebar
 *
 * Desktop/tablet navigation rail. Phones never render this – they use
 * BottomNav instead – so there is no mobile drawer branch here.
 *
 * The nav is grouped into three or four scannable sections (app-shell.md §2)
 * rather than one flat run of rows, and it is a plain list of links rather than
 * an antd Menu: the selected row is the product's one accent tile (a solid
 * --color-primary square with the glyph knocked out), and every other row is a
 * bare stroke glyph, which the Menu's own markup could not express cleanly.
 */

import { Layout } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { IconInfo } from '../components/icons';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import { getMenuItems } from './MenuItems';
import { APP_INFO } from '../../core/constants';
import { IconTile } from '../components/primitives';
import { useBranding, useBrandLogo, useTheme } from '../../core/hooks';
import './layout.css';

const { Sider } = Layout;

// Sections render in this order; within a section the menu order is preserved.
const GROUP_ORDER = ['daily', 'academic', 'people', 'content'];

/**
 * Flatten a menu entry that may carry `children` (the teacher question bank)
 * into navigable leaves, inheriting the parent's icon and group so the two
 * destinations read as two rows of the Content section.
 */
const flatten = (item) =>
  item.children?.length
    ? item.children.map((child) => ({
        ...child,
        icon: child.icon ?? item.icon,
        group: item.group,
      }))
    : [item];

const SidebarBody = ({ user, collapsed, onOpenChangelog }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { logoUrl, logoDarkUrl, centreName } = useBranding();
  const { isDark } = useTheme();
  const brandLogoSrc = useBrandLogo(isDark);

  const menuItems = useMemo(() => getMenuItems(user?.roleName || '', t), [user?.roleName, t]);
  const flat = useMemo(() => menuItems.flatMap(flatten), [menuItems]);

  const groups = useMemo(
    () =>
      GROUP_ORDER.map((key) => ({ key, items: flat.filter((item) => item.group === key) })).filter(
        (group) => group.items.length,
      ),
    [flat],
  );
  const footItems = useMemo(() => flat.filter((item) => item.group === 'account'), [flat]);

  const selectedPath = useMemo(() => {
    const exact = flat.find((item) => item.path === location.pathname);
    if (exact) return exact.path;
    const prefixes = flat
      .filter((item) => item.path !== '/' && location.pathname.startsWith(`${item.path}/`))
      .sort((a, b) => b.path.length - a.path.length);
    return prefixes[0]?.path ?? null;
  }, [flat, location.pathname]);

  // A centre with an uploaded logo shows it; otherwise a monogram on the accent
  // tile — never the vendor mark, which was W-9.
  const hasCentreLogo = Boolean((isDark && logoDarkUrl) || logoUrl);
  const displayName = centreName || APP_INFO.NAME;
  const monogram = displayName.trim().charAt(0).toUpperCase() || 'L';

  const renderRow = (item) => {
    const selected = selectedPath === item.path;
    const label = typeof item.label === 'string' ? item.label : undefined;
    return (
      <Link
        key={item.path}
        to={item.path}
        className={`lms-sidebar__row ${selected ? 'is-selected' : ''}`}
        title={collapsed ? label : undefined}
        aria-current={selected ? 'page' : undefined}
      >
        {selected ? (
          <IconTile
            icon={item.icon}
            tone="primary"
            variant="solid"
            size="sm"
            className="lms-sidebar__row-tile"
          />
        ) : (
          <span className="lms-sidebar__row-glyph" aria-hidden="true">
            {item.icon}
          </span>
        )}
        {!collapsed && <span className="lms-sidebar__row-label">{item.label}</span>}
      </Link>
    );
  };

  return (
    <div className={`lms-sidebar ${collapsed ? 'lms-sidebar--collapsed' : ''}`}>
      <div className={`lms-sidebar__brand ${collapsed ? 'is-compact' : ''}`}>
        {hasCentreLogo ? (
          <img src={brandLogoSrc} alt={displayName} className="lms-sidebar__brand-logo" />
        ) : (
          <span className="lms-sidebar__brand-monogram" aria-hidden="true">
            {monogram}
          </span>
        )}
        {!collapsed && <span className="lms-sidebar__brand-name">{displayName}</span>}
      </div>

      <nav className="lms-sidebar__nav" aria-label={t('common.primaryNavigation')}>
        {groups.map((group) => (
          <div className="lms-sidebar__group" key={group.key}>
            {collapsed ? (
              <div className="lms-sidebar__group-rule" aria-hidden="true" />
            ) : (
              <div className="lms-sidebar__group-label">{t(`navigation.section.${group.key}`)}</div>
            )}
            {group.items.map(renderRow)}
          </div>
        ))}
        {/* Flex spacer that pushes the foot down. The girih lattice is on the
            whole .lms-sidebar column now (W4.1), so it is not repeated here. */}
        <div className="lms-sidebar__tail" aria-hidden="true" />
      </nav>

      <div className="lms-sidebar__foot">
        {footItems.map(renderRow)}
        <button
          type="button"
          className="lms-sidebar__version"
          onClick={onOpenChangelog}
          title={`${t('common.platformVersion')} v${APP_INFO.VERSION}`}
        >
          <IconInfo />
          {!collapsed && (
            <span className="lms-sidebar__version-text">
              {t('common.platformVersion')} <span className="lms-figure">v{APP_INFO.VERSION}</span>
            </span>
          )}
        </button>
      </div>
    </div>
  );
};

SidebarBody.propTypes = {
  user: PropTypes.object,
  collapsed: PropTypes.bool,
  onOpenChangelog: PropTypes.func,
};

const Sidebar = ({ user, collapsed, onOpenChangelog }) => (
  <Sider
    collapsible
    collapsed={collapsed}
    trigger={null}
    width={248}
    collapsedWidth={72}
    breakpoint="lg"
    className="lms-sider"
  >
    <SidebarBody user={user} collapsed={collapsed} onOpenChangelog={onOpenChangelog} />
  </Sider>
);

Sidebar.propTypes = {
  user: PropTypes.object,
  collapsed: PropTypes.bool,
  onOpenChangelog: PropTypes.func,
};

export default Sidebar;
